import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { pageComponent } from '../../higher-order-components/pageComponent'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import Loading from '../../components/process-state-notifications/Loading'
import { getUrlQueryObject } from '../../../utils/urls'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import { intl } from '../../../i18n'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { useDialogState } from '../../components/dialogs/useDialogState'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { Header } from './components/Header'
import SprintReportBreadcrumb from './SprintReportBreadcrumb'
import { usePageState } from './hooks/pageState'
import {
  SprintReportSearchCondition,
  useSearchCondition,
} from './hooks/searchCondition'
import { useSprintReportData } from './hooks/sprintReportData'
import { useSprintReportBulkSheetState } from './hooks/bulkSheetState'
import { useSprintReportExcel } from './hooks/excel'
import { useSprintReportGridOptions } from './gridOptions'

// 列設定ポッパー / Excel エクスポート列選択ダイアログから除外する内部列。
// rowNumber は ag-grid のシステム列扱いで、Excel 出力対象には含めない。
const NON_EXPORTABLE_COL_IDS = ['rowNumber']

const SprintReport = ({ uuid: functionUuid }: PageProps) => {
  const { projectUuid } = useProjectPrivateContext()
  const ref = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const columnSetting = useColumnSetting()
  const excelApi = useSprintReportExcel(gridApi)
  // 旧 BulkSheet 版に合わせて「保存済み検索条件」と「保存済み列設定」は別々の UI state キーで保存する。
  // 同じ SavedUIStateDialog コンポーネントを 2 インスタンス使い分け、保存先と表示内容を分離する。
  const [
    savedSearchConditionDialogOpen,
    openSavedSearchConditionDialog,
    closeSavedSearchConditionDialog,
  ] = useDialogState()
  const [
    savedColumnSettingDialogOpen,
    openSavedColumnSettingDialog,
    closeSavedColumnSettingDialog,
  ] = useDialogState()

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api)
  }, [])

  const initialSearchCondition = useMemo(() => {
    const query = getUrlQueryObject()
    return {
      rootWbsItemUuid:
        typeof query?.rootWbsItem === 'string' ? query.rootWbsItem : undefined,
    }
  }, [])

  const pageStateApi = usePageState(functionUuid)
  const searchConditionApi = useSearchCondition(
    functionUuid,
    projectUuid,
    initialSearchCondition
  )
  const dataApi = useSprintReportData()
  const workloadUnitState = useWorkloadUnit(pageStateApi.workloadUnit)
  const bulkSheetStateApi = useSprintReportBulkSheetState(
    gridApi,
    functionUuid,
    projectUuid
  )

  // ag-grid の getContextMenuItems は gridOptions に閉じ込めた callback として渡るが、
  // searchCondition を直接依存に取ると searchCondition が変わるたび gridOptions も作り直され、
  // BulkSheetView 経由で AgGridReact に渡る options 参照が毎回変わってしまう。
  // 参照は固定したまま「呼ばれたタイミングで最新の searchCondition を返す」ように ref パターンで返却する。
  const searchConditionRef = useRef(searchConditionApi.searchCondition)
  searchConditionRef.current = searchConditionApi.searchCondition
  const getCurrentSearchCondition = useCallback(
    () => searchConditionRef.current,
    []
  )

  const { gridOptions, refreshColumns } = useSprintReportGridOptions(
    getCurrentSearchCondition,
    gridApi,
    pageStateApi,
    workloadUnitState
  )

  // 検索ボタン / パンくず / 保存済み検索条件適用などで「現在の searchCondition に部分上書き → 即時データ取得」する操作が複数箇所にあるので、
  // ヘルパーに集約する。setState は非同期なので、patch を渡してマージ済みの値で refresh する。
  const refreshWith = useCallback(
    (patch?: Partial<SprintReportSearchCondition>) => {
      const merged = patch
        ? { ...searchConditionApi.searchCondition, ...patch }
        : searchConditionApi.searchCondition
      dataApi.refresh(projectUuid, merged)
    },
    [projectUuid, searchConditionApi.searchCondition, dataApi]
  )

  const onClickSearch = useCallback(() => {
    refreshWith()
  }, [refreshWith])

  // 検索条件 (uistate) の復元完了時とプロジェクト切替時に自動でデータ取得する。
  // ユーザーが条件を変えた直後は再取得しない（検索ボタン押下まで）ので、
  // 依存は initialized と projectUuid のみに絞る。
  useEffect(() => {
    if (!searchConditionApi.initialized) return
    dataApi.refresh(projectUuid, searchConditionApi.searchCondition)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 自動取得は復元完了/プロジェクト切替の瞬間のみ
  }, [searchConditionApi.initialized, projectUuid])

  // 稼働日カレンダー更新時、および表示のみに影響する条件 (timeGrain / startDayOfWeek) 切替時に列を再生成する。
  // 検索ボタンを押さなくても即時反映する（旧 BulkSheet 版と同じ挙動）。
  // 一方 dateTerm / groupKey は再取得が必要なので、検索ボタンで workingDayCalendars が更新されたタイミングで反映する。
  useEffect(() => {
    refreshColumns(
      searchConditionApi.searchCondition,
      dataApi.workingDayCalendars
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 列再生成のトリガーは workingDayCalendars と表示系条件のみ
  }, [
    dataApi.workingDayCalendars,
    searchConditionApi.searchCondition.timeGrain,
    searchConditionApi.searchCondition.startDayOfWeek,
  ])

  // 集計フィールドや単位の切替はセル再計算のみで反映するため、
  // ag-grid v33+ の正規 API である setGridOption('context', ...) で更新してから refreshCells する。
  // gridOptions.context を直接 mutate しても valueGetter には伝わらないため必ず API 経由で渡す。
  useEffect(() => {
    if (!gridApi) return
    gridApi.setGridOption('context', {
      pageState: pageStateApi,
      workloadUnitState,
    })
    gridApi.refreshCells({ force: true })
  }, [
    gridApi,
    pageStateApi.aggregateField,
    pageStateApi.workloadUnit,
    workloadUnitState,
    pageStateApi,
  ])

  // rowHeight プロパティを更新しただけでは既に描画済みの行は元の高さのまま残るため、
  // 明示的に resetRowHeights を呼んで再計算させる。
  useEffect(() => {
    gridApi?.resetRowHeights()
  }, [gridApi, pageStateApi.rowHeight])

  return (
    <PageArea>
      {dataApi.appliedRootWbsItemUuid && (
        <SprintReportBreadcrumb
          rootWbsItemUuid={dataApi.appliedRootWbsItemUuid}
          onSelectBreadcrumb={(_event, breadcrumb) => {
            // パンくず選択は「ルート WBS を切り替えて即再取得する」操作。
            // 旧 BulkSheet 版では URL ナビゲーションで自動的に再取得していたため、新版でも即時に refresh して挙動を合わせる。
            searchConditionApi.setRootWbsItemUuid(breadcrumb.wbsItemUuid)
            refreshWith({ rootWbsItemUuid: breadcrumb.wbsItemUuid })
          }}
        />
      )}
      <Header
        projectUuid={projectUuid}
        searchConditionApi={searchConditionApi}
        pageStateApi={pageStateApi}
        onClickSearch={onClickSearch}
        onClickSavedSearchCondition={openSavedSearchConditionDialog}
        filteredColumns={bulkSheetStateApi.filteredColumns}
        onDeleteFilteredColumn={bulkSheetStateApi.onDeleteFilteredColumn}
        resetFilters={bulkSheetStateApi.resetFilters}
        sortColumnsState={bulkSheetStateApi.sortColumnsState}
        onDeleteSortedColumn={bulkSheetStateApi.onDeleteSortedColumn}
        onDeleteSortedAllColumns={bulkSheetStateApi.onDeleteSortedAllColumns}
        onChangeSortColumnState={bulkSheetStateApi.onChangeSortColumnState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        onClickExport={excelApi.openDialog}
        rowHeight={pageStateApi.rowHeight}
        onChangeRowHeight={pageStateApi.setRowHeight}
      />
      <BulkSheetView
        ref={ref}
        gridOptions={gridOptions}
        rowData={dataApi.rowData}
        rowHeight={pageStateApi.rowHeight}
        onGridReady={onGridReady}
        onFirstDataRendered={bulkSheetStateApi.onFirstDataRendered}
        onSortChanged={bulkSheetStateApi.onSortChanged}
        onColumnMoved={bulkSheetStateApi.onColumnMoved}
        onColumnResized={bulkSheetStateApi.onColumnResized}
        onColumnVisible={bulkSheetStateApi.onColumnVisible}
        onFilterChanged={bulkSheetStateApi.onFilterChanged}
      />
      <ColumnSettingPopper
        open={columnSetting.isOpen}
        anchorEl={columnSetting.anchorEl}
        close={columnSetting.close}
        gridApi={gridApi ?? undefined}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={openSavedColumnSettingDialog}
        initializeColumnState={() => {
          // 列状態 / フィルタ / ソートを一括で初期化する。ag-grid の reset*** は
          // 対応イベントを発火させないので、保存済み state と派生 state を明示的にクリアする。
          gridApi?.resetColumnState()
          gridApi?.setFilterModel(null)
          bulkSheetStateApi.saveColumnState([])
          bulkSheetStateApi.saveFilterState({})
          bulkSheetStateApi.resetDerivedState()
        }}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetStateApi.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={savedSearchConditionDialogOpen}
        onClose={closeSavedSearchConditionDialog}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState ?? selectedUIState
          // 検索条件保存には任意で列状態 / フィルタも同梱できる（旧 BulkSheet 版の挙動に合わせる）。
          bulkSheetStateApi.applyUIState(uiStateToApply)
          searchConditionApi.applySearchCondition(
            uiStateToApply.searchCondition
          )
          // 検索条件を復元したら検索ボタン操作と同じく即座にデータ取得する。
          // applySearchCondition の setState は非同期なので、merged な値で refresh を呼ぶ。
          if (uiStateToApply.searchCondition) {
            refreshWith(uiStateToApply.searchCondition)
          }
          closeSavedSearchConditionDialog()
        }}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateSearchCondition}
        title={intl.formatMessage({
          id: 'wbsItemSearchFilterPanel.saved.condition',
        })}
        sharable={false}
        currentUIState={{
          searchCondition: searchConditionApi.searchCondition,
          columnState: bulkSheetStateApi.columnState,
          filterState: bulkSheetStateApi.filterState,
        }}
        searchPlaceholder={intl.formatMessage({
          id: 'savedUIState.content.search.searchConditionPlaceholder',
        })}
        nameLabel={intl.formatMessage({
          id: 'savedUIState.content.list.searchConditionName',
        })}
      />
      <SavedUIStateDialog
        open={savedColumnSettingDialogOpen}
        onClose={closeSavedColumnSettingDialog}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState ?? selectedUIState
          // 列設定保存は列状態 + フィルタのみを復元する。検索条件は触らない。
          bulkSheetStateApi.applyUIState(uiStateToApply)
          closeSavedColumnSettingDialog()
        }}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
        })}
        sharable={false}
        currentUIState={{
          columnState: bulkSheetStateApi.columnState,
          filterState: bulkSheetStateApi.filterState,
        }}
      />
      {excelApi.dialogOpen && (
        <MultiSelectDialog
          onClose={excelApi.closeDialog}
          onSubmit={(_all, selected) => {
            excelApi.onExportExcel(selected.map(col => col.value))
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridApi?.getColumns() ?? [])
              .filter(
                column => !NON_EXPORTABLE_COL_IDS.includes(column.getColId())
              )
              .map(column => ({
                value: column.getColId(),
                displayName:
                  gridApi?.getDisplayNameForColumn(column, null) ?? '',
                defaultChecked: column.isVisible(),
              }))
          }}
          hideIcon={true}
        />
      )}
      <Loading isLoading={dataApi.loading} elem={ref.current} />
    </PageArea>
  )
}

export default projectPrivate(pageComponent(SprintReport))
